/**
 * The hero's particle halo: `setupHeroHalo` from `clients/marketing-pages/app.js:364-775` @ 654a67e447, as a plain
 * module that owns one canvas and nothing else. The maths, the seeded RNG and every constant are
 * production's, unchanged. What changed, and only this:
 *
 * - it is handed its canvas instead of finding `#halo`, and it measures and observes that canvas, not
 *   `.hero`/`.hero-stage`. The canvas's CSS box is its positioned container's (`<HeroHalo>` lays it
 *   out), so the old `canvas.style.left/width/height` writes are gone;
 * - the particle colour is the canvas's own computed `color`, set by a utility in `<HeroHalo>`, in
 *   place of the `--hero-particle-rgb` root property;
 * - reduced motion is an argument. `<HeroHalo>` subscribes to the media query and recreates the halo
 *   when it changes, which is what production's `change` listener did (`resetScene()` + `start()`);
 * - clicks and the rotator's pulse arrive through `burst()` and `pulse()`, called by React handlers,
 *   not through a listener on `.hero` or `window.__SONIC_HALO__`;
 * - the `data-*` debugging attributes and the `sonicjobs:themechange` listener are dropped: nothing
 *   reads the former, and nothing anywhere dispatches the latter (no theme toggle ships);
 * - `dispose()` undoes every observer, listener and pending frame.
 *
 * This file must stay class-string free: Tailwind does not scan `.ts` files.
 */

export type Halo = {
  /** Four concentric rings from a click, at viewport coordinates. */
  burst: (clientX: number, clientY: number) => void
  /** One strong wave from the centre; the rotator calls it as each platform name completes. */
  pulse: () => void
  dispose: () => void
}

type Dust = { angle: number; distance: number; velocity: number; alpha: number; size: number }
type Wave = {
  startedAt: number
  duration: number
  strength: number
  seed: number
  gaps: number
  size: number
  originX: number
  originY: number
  startRadius: number
  maximumRadius: number
  interactive: boolean
}
type WaveOptions = Partial<
  Pick<Wave, 'duration' | 'gaps' | 'size' | 'originX' | 'originY' | 'startRadius' | 'maximumRadius' | 'interactive'>
> & {
  delay?: number
}

/** `rgb(37, 86, 216)` → `37, 86, 216`, the shape production's `rgba(${color}, ${alpha})` expects. */
function channels(color: string): string {
  return (
    color
      .match(/\d+(\.\d+)?/g)
      ?.slice(0, 3)
      .join(', ') || '255, 255, 255'
  )
}

/** Returns null when the browser gives no 2D context; the canvas then stays blank. */
export function createHalo(canvas: HTMLCanvasElement, { reducedMotion }: { reducedMotion: boolean }): Halo | null {
  const context = canvas.getContext('2d')
  if (!context) return null

  const tau = Math.PI * 2
  const dustColor = channels(getComputedStyle(canvas).color)
  const waveColor = dustColor
  const ambientRingScales = [0.08, 0.125, 0.18, 0.245, 0.32, 0.405, 0.5, 0.605, 0.72, 0.845, 0.98, 1.125]
  let width = 0
  let height = 0
  let ratio = 1
  let centerX = 0
  let centerY = 0
  let radius = 0
  let elapsed = 0
  let nextWave = 0
  let lastFrame = 0
  let frameId = 0
  let visible = true
  let seed = 948721
  let dust: Dust[] = []
  let waves: Wave[] = []

  const random = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0
    return seed / 4294967296
  }
  const between = (minimum: number, maximum: number): number => minimum + random() * (maximum - minimum)
  const hash = (value: number): number => {
    const sine = Math.sin(value) * 43758.5453123
    return sine - Math.floor(sine)
  }
  const smoothstep = (start: number, end: number, value: number): number => {
    const progress = Math.min(1, Math.max(0, (value - start) / (end - start)))
    return progress * progress * (3 - 2 * progress)
  }
  const canAnimate = (): boolean => !reducedMotion && visible && document.visibilityState === 'visible'

  const copyDamping = (x: number): number => {
    if (width < 700) return 0.68
    return 0.26 + 0.74 * smoothstep(width * 0.4, width * 0.72, x)
  }

  const makeDust = (anywhere = false): Dust => {
    const angle = between(0, tau)
    return {
      angle,
      distance: anywhere ? between(radius * 0.2, radius * 1.42) : between(radius * 0.18, radius * 0.34),
      velocity: between(5, 12),
      alpha: between(0.06, 0.14),
      size: random() < 0.84 ? 0.95 : 1.45,
    }
  }

  const addWave = (strength = 0.62, age = 0, options: WaveOptions = {}): void => {
    const interactive = options.interactive === true
    waves.push({
      startedAt: elapsed - age + (options.delay || 0),
      duration: options.duration || (strength > 0.8 ? between(6.2, 6.8) : between(7.2, 8.2)),
      strength,
      seed: between(0, 1000),
      gaps: options.gaps ?? (strength > 0.8 ? 0.16 : 0.3),
      size: options.size ?? (strength > 0.8 ? 1.55 : 1.15),
      originX: options.originX ?? centerX,
      originY: options.originY ?? centerY,
      startRadius: options.startRadius ?? radius * 0.055,
      maximumRadius: options.maximumRadius ?? radius * 1.48,
      interactive,
    })
    if (waves.length > 14) waves.shift()
  }

  const addConcentricBurst = (originX: number, originY: number): void => {
    const maximumRadius = Math.hypot(Math.max(originX, width - originX), Math.max(originY, height - originY)) * 1.04
    const rings = [
      { delay: 0, strength: 1, duration: 4.15, gaps: 0.13, size: 1.7 },
      { delay: 0.16, strength: 0.88, duration: 4.35, gaps: 0.17, size: 1.55 },
      { delay: 0.32, strength: 0.76, duration: 4.55, gaps: 0.21, size: 1.4 },
      { delay: 0.48, strength: 0.64, duration: 4.75, gaps: 0.25, size: 1.25 },
    ]

    rings.forEach((ring) => {
      addWave(ring.strength, 0, { ...ring, originX, originY, startRadius: 4, maximumRadius, interactive: true })
    })
  }

  const resetScene = (): void => {
    dust = Array.from({ length: width < 700 ? 36 : 62 }, () => makeDust(true))
    waves = []
    addWave(0.62, 1.2)
    addWave(0.52, 3.8)
    addWave(0.46, 6.4)
    nextWave = between(1.7, 2.2)
  }

  const clear = (): void => context.clearRect(0, 0, width, height)

  const drawDot = (x: number, y: number, size: number, color: string, alpha: number): void => {
    if (alpha < 0.008 || x < -4 || x > width + 4 || y < -4 || y > height + 4) {
      return
    }
    context.fillStyle = `rgba(${color}, ${alpha})`
    context.beginPath()
    context.arc(x, y, size, 0, tau)
    context.fill()
  }

  const drawAmbientShell = (): void => {
    const narrow = width < 700
    ambientRingScales.forEach((scale, ringIndex) => {
      const shellRadius = radius * scale
      const dotCount = Math.min(220, Math.max(72, Math.round(shellRadius / (narrow ? 3.8 : 3.45))))
      const drift = elapsed * (ringIndex % 2 === 0 ? 0.009 : -0.007)

      for (let dotIndex = 0; dotIndex < dotCount; dotIndex += 1) {
        const noise = hash(ringIndex * 83.4 + dotIndex * 6.73)
        if (noise < 0.11) continue
        const angle = (tau * dotIndex) / dotCount + ringIndex * 0.27 + drift
        const ripple = Math.sin(angle * (2 + (ringIndex % 4)) + elapsed * 0.28) * (0.5 + ringIndex * 0.045)
        const dottedRadius = shellRadius + ripple
        const x = centerX + Math.cos(angle) * dottedRadius
        const y = centerY + Math.sin(angle) * dottedRadius
        const alpha = (narrow ? 0.2 : 0.17) - ringIndex * 0.006
        drawDot(x, y, ringIndex < 3 ? 1.22 : 1.08, waveColor, alpha * copyDamping(x))
      }
    })
  }

  const drawScene = (delta = 0): void => {
    clear()
    drawAmbientShell()

    dust.forEach((particle, index) => {
      particle.distance += particle.velocity * delta
      if (particle.distance > radius * 1.48) {
        dust[index] = makeDust()
        return
      }

      const x = centerX + Math.cos(particle.angle) * particle.distance
      const y = centerY + Math.sin(particle.angle) * particle.distance
      const edgeFade = Math.min(1, (radius * 1.48 - particle.distance) / (radius * 0.24))
      drawDot(x, y, particle.size, dustColor, particle.alpha * edgeFade * copyDamping(x))
    })

    for (let waveIndex = waves.length - 1; waveIndex >= 0; waveIndex -= 1) {
      const wave = waves[waveIndex]
      const progress = (elapsed - wave.startedAt) / wave.duration
      if (progress >= 1) {
        waves.splice(waveIndex, 1)
        continue
      }
      if (progress < 0) continue

      const eased = 1 - Math.pow(1 - progress, 1.55)
      const waveRadius = wave.startRadius + eased * (wave.maximumRadius - wave.startRadius)
      const fadeIn = Math.min(1, progress / 0.07)
      const fadeOut = progress < 0.58 ? 1 : Math.pow(Math.max(0, 1 - (progress - 0.58) / 0.42), 1.35)
      const alpha = wave.strength * 0.28 * fadeIn * fadeOut
      const dotCount = Math.min(260, Math.max(104, Math.round(wave.maximumRadius / 3.4)))

      for (let dotIndex = 0; dotIndex < dotCount; dotIndex += 1) {
        const noise = hash(wave.seed + dotIndex * 7.31)
        if (noise < wave.gaps) continue
        const angle = (tau * dotIndex) / dotCount + wave.seed
        const dottedRadius = waveRadius + (noise - 0.5) * 3.6
        const x = wave.originX + Math.cos(angle) * dottedRadius
        const y = wave.originY + Math.sin(angle) * dottedRadius
        drawDot(
          x,
          y,
          wave.size,
          waveColor,
          alpha *
            (0.7 + 0.3 * hash(wave.seed + dotIndex * 1.91)) *
            (wave.interactive ? Math.max(0.55, copyDamping(x)) : copyDamping(x)),
        )
      }
    }
  }

  const drawStatic = (): void => {
    clear()
    ambientRingScales.forEach((scale, ringIndex) => {
      const staticRadius = radius * scale
      const dotCount = Math.min(220, Math.max(76, Math.round(staticRadius / 3.45)))
      for (let dotIndex = 0; dotIndex < dotCount; dotIndex += 1) {
        const noise = hash(ringIndex * 77.7 + dotIndex * 7.31)
        if (noise < 0.1) continue
        const angle = (tau * dotIndex) / dotCount + ringIndex * 0.31
        const ripple = Math.sin(angle * (2 + (ringIndex % 4))) * (0.5 + ringIndex * 0.045)
        const dottedRadius = staticRadius + ripple
        const x = centerX + Math.cos(angle) * dottedRadius
        const y = centerY + Math.sin(angle) * dottedRadius
        drawDot(x, y, ringIndex < 3 ? 1.22 : 1.1, waveColor, (0.2 - ringIndex * 0.007) * copyDamping(x))
      }
    })
  }

  const resize = (): void => {
    const bounds = canvas.getBoundingClientRect()
    const nextWidth = Math.round(bounds.width)
    const nextHeight = Math.round(bounds.height)
    if (!nextWidth || !nextHeight) return
    if (nextWidth === width && nextHeight === height) return

    width = nextWidth
    height = nextHeight
    ratio = Math.min(2, window.devicePixelRatio || 1)
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    context.setTransform(ratio, 0, 0, ratio, 0, 0)

    const narrow = width < 700
    centerX = width * (narrow ? 0.74 : 0.78)
    centerY = height * (narrow ? 0.27 : 0.42)
    radius = Math.min(width * (narrow ? 0.9 : 0.55), height * (narrow ? 0.36 : 0.62))
    resetScene()
    if (reducedMotion) drawStatic()
    else drawScene()
  }

  const stop = (): void => {
    if (frameId) window.cancelAnimationFrame(frameId)
    frameId = 0
  }

  const frame = (now: number): void => {
    if (!canAnimate()) {
      stop()
      return
    }

    const delta = Math.min(0.05, (now - lastFrame) / 1000 || 0)
    lastFrame = now
    elapsed += delta
    nextWave -= delta
    if (nextWave <= 0) {
      if (waves.length < 6) addWave()
      nextWave = between(2.3, 2.9)
    }
    drawScene(delta)
    frameId = window.requestAnimationFrame(frame)
  }

  const start = (): void => {
    stop()
    if (reducedMotion) {
      drawStatic()
      return
    }
    if (!canAnimate()) return
    lastFrame = performance.now()
    frameId = window.requestAnimationFrame(frame)
  }

  const pulse = (): void => {
    if (!canAnimate()) return
    addWave(1)
    nextWave = between(2.3, 2.9)
  }

  const burst = (clientX: number, clientY: number): void => {
    const bounds = canvas.getBoundingClientRect()
    const originX = clientX - bounds.left
    const originY = clientY - bounds.top
    if (!canAnimate() || !Number.isFinite(originX) || !Number.isFinite(originY)) return
    addConcentricBurst(Math.min(width, Math.max(0, originX)), Math.min(height, Math.max(0, originY)))
  }

  // Production's fallbacks, kept: KaiOS 2.5, in `defaults`, has neither observer.
  // eslint-disable-next-line compat/compat -- guarded by the `in` check
  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(resize) : undefined
  if (resizeObserver) resizeObserver.observe(canvas)
  else window.addEventListener('resize', resize)

  const intersectionObserver =
    'IntersectionObserver' in window
      ? // eslint-disable-next-line compat/compat -- guarded by the `in` check
        new IntersectionObserver(
          ([entry]) => {
            visible = entry.isIntersecting
            start()
          },
          { threshold: 0 },
        )
      : undefined
  intersectionObserver?.observe(canvas)

  document.addEventListener('visibilitychange', start)

  resize()
  start()

  return {
    burst,
    pulse,
    dispose: (): void => {
      stop()
      resizeObserver?.disconnect()
      window.removeEventListener('resize', resize)
      intersectionObserver?.disconnect()
      document.removeEventListener('visibilitychange', start)
    },
  }
}
